import React, { useState } from "react";
import useAiSettings from "./useAiSettings";
import useGeneralSettings from "./useGeneralSettings";

export default function PreferencesModal({ open, onClose, initialTab = "ai" }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const {
    useAi, setUseAi,
    newKey, setNewKey,
    clearKey, setClearKey,
    model, setModel,
    hasKey,
    orgHasKey,
    orgModel,
    loading,
    savedFlag,
    error,
    save,
    reset,
  } = useAiSettings();
  const general = useGeneralSettings();

  if (!open) return null;

  const aiAvailable = hasKey || orgHasKey;

  return (
    <div className="prefs-overlay" onMouseDown={onClose}>
      <div
        className="prefs-window"
        role="dialog"
        aria-label="Preferences"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="prefs-toolbar">
          <button
            className={`prefs-tab ${activeTab === "general" ? "active" : ""}`}
            onClick={() => setActiveTab("general")}
          >
            General
          </button>
          <button
            className={`prefs-tab ${activeTab === "ai" ? "active" : ""}`}
            onClick={() => setActiveTab("ai")}
          >
            AI
          </button>
          <button
            className={`prefs-tab ${activeTab === "advanced" ? "active" : ""}`}
            onClick={() => setActiveTab("advanced")}
          >
            Advanced
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="drawer-close-btn"
            onClick={onClose}
            aria-label="Close Preferences"
            title="Close"
          >
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
              <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <div className="prefs-body">
          <aside className="prefs-sidebar" aria-label="Sections">
            <button className={`prefs-side-item ${activeTab === "ai" ? "active" : ""}`} onClick={() => setActiveTab("ai")}>
              AI Settings
            </button>
            <button className={`prefs-side-item ${activeTab === "general" ? "active" : ""}`} onClick={() => setActiveTab("general")}>
              General
            </button>
            <button className={`prefs-side-item ${activeTab === "advanced" ? "active" : ""}`} onClick={() => setActiveTab("advanced")}>
              Advanced
            </button>
          </aside>
          <section className="prefs-content">
            {activeTab === "ai" && (
              <div>
                <h2>AI Settings</h2>
                <p className="prefs-note">
                  {useAi
                    ? "AI is enabled. Type natural language queries like 'invoices from last month' and the server will translate them."
                    : "AI is off. Enable it to search using natural language. Requires an API key (personal or organization)."}
                </p>

                {/* Organization key status */}
                <div className="prefs-info-row" style={{ marginBottom: 12, fontSize: 13, color: "#555" }}>
                  <span style={{ marginRight: 8 }}>Organization key:</span>
                  {orgHasKey
                    ? <span style={{ color: "#2a7a2a", fontWeight: 600 }}>Available{orgModel ? ` (${orgModel})` : ""}</span>
                    : <span style={{ color: "#999" }}>Not configured</span>}
                </div>

                <label className="form-row" style={{ alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <input
                    type="checkbox"
                    checked={useAi}
                    onChange={(e) => setUseAi(e.target.checked)}
                    disabled={loading}
                  />
                  <span>Enable AI search (server-side)</span>
                  {!aiAvailable && !hasKey && !newKey && (
                    <span style={{ fontSize: 12, color: "#c0392b", marginLeft: 4 }}>
                      — no key configured
                    </span>
                  )}
                </label>

                {/* Personal key section */}
                <div className="form-row-col" style={{ marginBottom: 8 }}>
                  <label className="form-label">
                    Personal API Key
                    <span style={{ fontWeight: 400, color: "#888", fontSize: 12, marginLeft: 6 }}>
                      (overrides organization key)
                    </span>
                  </label>
                  {hasKey && !clearKey ? (
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        type="password"
                        value="••••••••••••••••••••••••"
                        readOnly
                        style={{ flex: 1, color: "#555" }}
                      />
                      <button
                        className="link-btn"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={() => { setClearKey(true); setNewKey(""); }}
                        disabled={loading}
                      >
                        Clear key
                      </button>
                    </div>
                  ) : (
                    <input
                      type="password"
                      value={newKey}
                      onChange={(e) => { setNewKey(e.target.value); setClearKey(false); }}
                      placeholder={clearKey ? "Key will be cleared on save" : "sk-... (leave blank to keep unchanged)"}
                      disabled={loading}
                    />
                  )}
                </div>

                <div className="form-row-col" style={{ marginBottom: 16 }}>
                  <label className="form-label">
                    Model
                    <span style={{ fontWeight: 400, color: "#888", fontSize: 12, marginLeft: 6 }}>
                      (optional override)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    placeholder={orgModel || "gpt-4o-mini"}
                    disabled={loading}
                  />
                </div>

                {error && <div style={{ color: "#c0392b", fontSize: 13, marginBottom: 8 }}>{error}</div>}

                <div className="form-actions">
                  <button className="link-btn" onClick={reset} disabled={loading}>Reset</button>
                  <button className="go-btn" onClick={save} disabled={loading}>
                    {loading ? "Saving..." : "Save"}
                  </button>
                </div>
                {savedFlag && <div className="prefs-saved">Saved</div>}

                <div className="prefs-hint" style={{ marginTop: 12 }}>
                  Keys are stored server-side per user. Your personal key takes priority over the
                  organization key. An administrator can set an organization-wide key in the Admin Hub.
                </div>
              </div>
            )}
            {activeTab === "general" && (
              <div>
                <h2>General</h2>
                <div className="form-row">
                  <input
                    id="pref_show_interpreted"
                    type="checkbox"
                    checked={general.showInterpretedPreview}
                    onChange={(e) => general.setShowInterpretedPreview(e.target.checked)}
                  />
                  <label htmlFor="pref_show_interpreted">
                    Show interpreted filter preview under the search box
                  </label>
                </div>
                <div className="form-row">
                  <input
                    id="pref_cmdenter_to_search"
                    type="checkbox"
                    checked={general.requireCmdEnterToSearch}
                    onChange={(e) => general.setRequireCmdEnterToSearch(e.target.checked)}
                  />
                  <label htmlFor="pref_cmdenter_to_search">
                    Require Cmd+Enter to run search (Enter edits only)
                  </label>
                </div>
                <div className="form-row">
                  <input
                    id="pref_show_doc_preview"
                    type="checkbox"
                    checked={general.showDocPreview}
                    onChange={(e) => general.setShowDocPreview(e.target.checked)}
                  />
                  <label htmlFor="pref_show_doc_preview">
                    Show document preview on hover in results
                  </label>
                </div>
                <div className="form-actions">
                  <button className="link-btn" onClick={general.reset}>Reset</button>
                  <button className="go-btn" onClick={general.save}>Save</button>
                </div>
                {general.savedFlag && <div className="prefs-saved">Saved</div>}
              </div>
            )}
            {activeTab === "advanced" && (
              <div style={{ color: "#666", fontSize: 14 }}>
                More settings coming soon.
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
